import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  normalizePermissionMap,
  type PermissionMap,
} from '@/lib/permissions';

export interface ClinicRoleRow {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  system_key: string | null;
  is_practitioner: boolean;
  require_journal_patient_confirm: boolean;
  created_at: string;
  updated_at: string;
  permissions: PermissionMap;
  member_count: number;
}

export const clinicRolesQueryKey = (clinicId: string | null | undefined) =>
  ['clinic-roles', clinicId] as const;

export async function fetchClinicRoles(clinicId: string): Promise<ClinicRoleRow[]> {
  const { data, error } = await supabase.rpc('list_clinic_roles', {
    p_clinic_id: clinicId,
  });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: String(row.id),
    clinic_id: String(row.clinic_id),
    name: String(row.name),
    description: row.description ?? null,
    is_system: Boolean(row.is_system),
    system_key: row.system_key ?? null,
    is_practitioner: Boolean(row.is_practitioner),
    require_journal_patient_confirm: Boolean(row.require_journal_patient_confirm),
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
    permissions: normalizePermissionMap(row.permissions),
    member_count: Number(row.member_count ?? 0),
  }));
}

export function useClinicRoles(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicRolesQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: () => fetchClinicRoles(clinicId!),
  });
}

export function useCreateClinicRole(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string; description?: string }) => {
      const { data, error } = await supabase.rpc('create_clinic_role', {
        p_clinic_id: clinicId!,
        p_name: vars.name,
        p_description: vars.description ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clinicRolesQueryKey(clinicId) });
    },
  });
}

export function useUpdateClinicRole(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      roleId: string;
      name?: string;
      description?: string;
      isPractitioner?: boolean;
      requireJournalPatientConfirm?: boolean;
    }) => {
      const { data, error } = await supabase.rpc('update_clinic_role', {
        p_role_id: vars.roleId,
        p_name: vars.name ?? null,
        p_description: vars.description ?? null,
        p_is_practitioner: vars.isPractitioner ?? null,
        p_require_journal_patient_confirm: vars.requireJournalPatientConfirm ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clinicRolesQueryKey(clinicId) });
    },
  });
}

export function useDeleteClinicRole(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase.rpc('delete_clinic_role', {
        p_role_id: roleId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clinicRolesQueryKey(clinicId) });
    },
  });
}

export function useSetClinicRolePermissions(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { roleId: string; permissions: PermissionMap }) => {
      const { data, error } = await supabase.rpc('set_clinic_role_permissions', {
        p_role_id: vars.roleId,
        p_permissions: vars.permissions,
      });
      if (error) throw error;
      return normalizePermissionMap(data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clinicRolesQueryKey(clinicId) });
    },
  });
}

export function useAssignMemberCustomRole(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { membershipId: string; customRoleId: string | null }) => {
      const { data, error } = await supabase.rpc('assign_member_custom_role', {
        p_membership_id: vars.membershipId,
        p_custom_role_id: vars.customRoleId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clinic-users'] });
      void qc.invalidateQueries({ queryKey: clinicRolesQueryKey(clinicId) });
    },
  });
}
